<template>
  <foldable-card
    non-foldable
    title="Shared Reports"
    description="Below you can view and manage your VendorProof Engage report sharing"
    style="border-top-width: 8px"
  >
    <div class="add-report-button-container">
      <v-spacer></v-spacer>
      <cta-button @click="reportDialogOpen = true">
        <v-icon left>mdi-email</v-icon>Share A Report
      </cta-button>
    </div>
    <v-data-table
      class="main-table"
      :headers="headers"
      :items="reports"
      hide-default-footer
      disable-sort
      :options="tableOptions"
    >
      <template v-slot:item="{ item, index }">
        <tr :key="`shared-reports-table-${index}`">
          <td>{{ item.startDate | formatDate }}</td>
          <td>{{ item.frequency | frequency }}</td>
          <td>
            <div v-for="(recipient, index) in item.recipients" :key="index">
              {{ recipient.companyName }}
            </div>
          </td>
          <td>
            <div v-for="(recipient, index) in item.recipients" :key="index">
              {{ recipient.contactName }}
            </div>
          </td>
          <td>
            <div v-for="(recipient, index) in item.recipients" :key="index">
              {{ recipient.email }}
            </div>
          </td>
          <td class="text-right">
            <v-btn
              class="primary--text tertiary mr-4"
              color="secondary"
              height="32px"
              width="32px"
              :title="
                `Edit Shared Report for ${
                  item.recipients.length
                    ? item.recipients[0].companyName
                    : 'No One'
                }`
              "
              depressed
              @click="editReport(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              class="primary--text tertiary"
              color="secondary"
              height="32px"
              width="32px"
              :title="
                `Delete Shared Report for ${
                  item.recipients.length
                    ? item.recipients[0].companyName
                    : 'No One'
                }`
              "
              depressed
              @click="deleteEmail(item.id)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-dialog v-model="deleteDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="#FE285B">mdi-alert-rhombus</v-icon>
          <v-btn icon absolute top right @click="closeDeleteEmailDialog">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          Are you sure you want to delete this scheduled email? This action
          cannot be undone.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteEmailDialog">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyDeleteEmail">
            Delete Email
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <shared-report-dialog
      v-model="reportDialogOpen"
      @added="loadData"
      ref="reportDialog"
    >
    </shared-report-dialog>
  </foldable-card>
</template>

<script>
import { scheduledReportAPI, scheduledReportByIdAPI } from "@/services/api";
import { get, _delete } from "@/services/request";
import { formatDate } from "@/services/format";
import hasNotifications from "@/mixins/has-notifications";
import FoldableCard from "@/components/FoldableCard";
import BlueCallToAction from "@/components/providertrust/buttons/BlueCallToAction";
import SharedReportDialog from "@/views/reports/SharedReportDialog";
import cloneDeep from "lodash/cloneDeep";

const tableOptions = {
  itemsPerPage: -1
};

const headers = [
  { text: "Date", value: "startDate" },
  { text: "Frequency", value: "frequency" },
  { text: "Client/Company", value: "companyName" },
  { text: "Contact", value: "contactName" },
  { text: "Email", value: "email" },
  { text: "", value: "", align: "end" }
];

const uiFriendlyFrequencyNames = {
  MONTHLY: "Monthly",
  ONE_TIME: "One Time"
};

export default {
  name: "SharedReports",
  props: ["isActive"],
  data: () => ({
    reports: [],

    reportToDelete: null,
    tableOptions,
    headers,
    deleteDialogOpen: false,
    reportDialogOpen: false
  }),
  components: {
    FoldableCard,
    "cta-button": BlueCallToAction,
    SharedReportDialog
  },
  mixins: [hasNotifications],
  filters: {
    formatDate,
    frequency: val => val && uiFriendlyFrequencyNames[val]
  },
  methods: {
    loadData() {
      get(scheduledReportAPI).then(response => {
        if (response && response.data) this.reports = response.data;
      });
    },
    editReport(report) {
      this.$refs.reportDialog.setReport(cloneDeep(report));
      this.reportDialogOpen = true;
    },
    deleteEmail(id) {
      this.reportToDelete = id;
      this.deleteDialogOpen = true;
    },
    closeDeleteEmailDialog() {
      this.deleteDialogOpen = false;
      this.reportToDelete = null;
    },
    actuallyDeleteEmail() {
      if (this.reportToDelete) {
        _delete(scheduledReportByIdAPI.getUrl(this.reportToDelete))
          .then(() => {
            this.showMessage("The scheduled report was deleted successfully");
            get(scheduledReportAPI).then(response => {
              if (response && response.data) this.reports = response.data;
            });
          })
          .catch(() =>
            this.showMessage({
              text: "Unable to delete scheduled report",
              mode: "error"
            })
          )
          .finally(this.closeDeleteEmailDialog());
      }
    }
  },
  watch: {
    isActive: {
      immediate: true,
      handler(isNowActive) {
        if (isNowActive) this.loadData();
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.report-container
  .report-title
    font-size: 40px
  .report-content
    img
      width: 494px
    font-size: 14px
    color: $darker-grey
    .section-title
      font-size: 20px
    .subtitle
      color: $subtitle
      font-weight: 600
    .label
      font-weight: bold
      vertical-align: top
      whitespace: pre
    address
      font-style: normal
      p
        margin: 0
.download-button-container
  display: flex
  justify-content: right

.v-card__actions
  justify-content: right

.v-dialog .v-text-field--filled.v-input--dense.v-text-field--single-line
  ::v-deep .v-input__control > .v-input__slot
    min-height: 52px !important
  ::v-deep i
    margin-top: 12px

.v-dialog .v-text-field.v-text-field--enclosed
  ::v-deep .v-text-field__details
    margin-bottom: 0px

.v-btn:not(.v-btn--round).v-size--default.add-recipient-btn
  padding: 0
  margin-top: -32px
  color: $primary
  font-weight: bold
  font-size: 16px
  line-height: 20px

.tertiary.v-btn:not(.v-btn--round).v-size--default
  padding: 0
  min-width: 0

.add-report-button-container
  display: flex
  justify-content: right
  margin-bottom: 16px
  margin-top: -32px
</style>
